// Localized playground copy and DOM text application.
// The default language is English so first load is internationally readable.
import { defaultHtml } from './defaults'

// Supported language codes for the playground UI.
export type PlaygroundLanguage = 'en' | 'tr'

// Complete localized copy contract consumed by static and dynamic UI paths.
export type PlaygroundMessages = {
  localeTag: string
  documentTitle: string
  languageLabel: string
  inputTabsLabel: string
  luisInputTab: string
  htmlInputTab: string
  compileButton: string
  applyButton: string
  previewHtmlLabel: string
  applyHtmlButton: string
  generatedCssHeading: string
  previewHeading: string
  luisSourceLabel: string
  generatedCssLabel: string
  cssGenerationTimeLabel: string
  previewHtmlUpdated: string
  previewHtmlUpdatedSanitized: string
  compilationSucceeded: string
  stylesApplied: string
  warningsLabel: string
  linesLabel: string
  charsLabel: string
  defaultHtml: string
}

const messageCatalog: Record<PlaygroundLanguage, PlaygroundMessages> = {
  en: {
    localeTag: 'en-US',
    documentTitle: 'LentyStyle Playground',
    languageLabel: 'Language',
    inputTabsLabel: 'Editor tabs',
    luisInputTab: 'Luis Input',
    htmlInputTab: 'HTML Input',
    compileButton: 'Compile',
    applyButton: 'Apply Style',
    previewHtmlLabel: 'Preview HTML',
    applyHtmlButton: 'Apply HTML',
    generatedCssHeading: 'Generated CSS',
    previewHeading: 'Preview',
    luisSourceLabel: 'Luis source',
    generatedCssLabel: 'Generated CSS',
    cssGenerationTimeLabel: 'CSS generation time',
    previewHtmlUpdated: 'Preview HTML updated.',
    previewHtmlUpdatedSanitized: 'Preview HTML updated. Script tags and inline events were ignored.',
    compilationSucceeded: 'Compilation succeeded',
    stylesApplied: 'Styles applied to the document',
    warningsLabel: 'warnings',
    linesLabel: 'lines',
    charsLabel: 'characters',
    defaultHtml,
  },
  tr: {
    localeTag: 'tr-TR',
    documentTitle: 'LentyStyle Playground',
    languageLabel: 'Dil',
    inputTabsLabel: 'Editör sekmeleri',
    luisInputTab: 'Luis Girdisi',
    htmlInputTab: 'HTML Girdisi',
    compileButton: 'Derle',
    applyButton: 'Stili Uygula',
    previewHtmlLabel: 'Önizleme HTML',
    applyHtmlButton: 'HTML Uygula',
    generatedCssHeading: 'Üretilen CSS',
    previewHeading: 'Önizleme',
    luisSourceLabel: 'Luis kaynağı',
    generatedCssLabel: 'Üretilen CSS',
    cssGenerationTimeLabel: 'CSS üretim süresi',
    previewHtmlUpdated: 'Önizleme HTML güncellendi.',
    previewHtmlUpdatedSanitized: 'Önizleme HTML güncellendi. Script etiketleri ve inline olaylar yok sayıldı.',
    compilationSucceeded: 'Derleme başarılı',
    stylesApplied: 'Stiller belgeye uygulandı',
    warningsLabel: 'uyarı',
    linesLabel: 'satır',
    charsLabel: 'karakter',
    defaultHtml,
  },
}

// Mutable public message object shared by render and action modules.
export const playgroundMessages: PlaygroundMessages = { ...messageCatalog.en }

// Apply a supported language to document chrome and future dynamic messages.
export function setPlaygroundLanguage(language: PlaygroundLanguage): void {
  Object.assign(playgroundMessages, messageCatalog[language])
  document.documentElement.lang = language
  document.title = playgroundMessages.documentTitle
  applyStaticTranslations()
}

// Format numbers with the active playground locale.
export function formatPlaygroundCount(value: number): string {
  return value.toLocaleString(playgroundMessages.localeTag)
}

// Resolve arbitrary select values into the supported language set.
export function normalizePlaygroundLanguage(value: string): PlaygroundLanguage {
  return value === 'tr' ? 'tr' : 'en'
}

// Push the active catalog into static DOM text and aria labels.
function applyStaticTranslations(): void {
  for (const element of document.querySelectorAll<HTMLElement>('[data-i18n]')) {
    const text = getMessageText(element.dataset.i18n)
    if (text !== null) {
      element.textContent = text
    }
  }

  for (const element of document.querySelectorAll<HTMLElement>('[data-i18n-aria-label]')) {
    const text = getMessageText(element.dataset.i18nAriaLabel)
    if (text !== null) {
      element.setAttribute('aria-label', text)
    }
  }
}

// Read a message by key without trusting arbitrary dataset values.
function getMessageText(key: string | undefined): string | null {
  if (!key || !Object.prototype.hasOwnProperty.call(playgroundMessages, key)) {
    return null
  }

  const value = playgroundMessages[key as keyof PlaygroundMessages]
  return typeof value === 'string' ? value : null
}
